import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { useEffect } from 'react';
import { db } from '../firebase.conf';
import { useAuthData } from '../LoginContext';
import { useTranslationData } from '../TranslationContext';
import { useAutoDetect } from "./useAutoDetect";

/**
 * Custom hook that manages text synchronization with Firebase.
 * This hook handles retrieving highlighted text from the browser via Firebase,
 * processes it for translation, and then clears it from Firestore.
 */
export const useFirebaseText = () => {
	const { iFrameQueryParam, setIFrameQueryParam } = useAuthData();
	const { setTextBefore, setInitialTextToTranslate } = useTranslationData();
	const { autoDetectLang } = useAutoDetect();
	const { auth0IdToken } = useAuthData();

	useEffect(() => {

		/**
		 * Clears the text field in the Firestore document after it has been processed
		 */
		const clearTextInFirestore = (id: string) =>
			setDoc(doc(db, 'public', id), { text: '' }, { merge: true });

		/**
		 * Sets up a real-time listener for text changes in Firestore
		 * When new text is detected:
		 * 1. Updates the text in the translation context
		 * 2. Triggers language detection
		 * 3. Stores the text for translation
		 * 4. Clears the text from Firestore
		 *
		 */
		const displayHighlightedTextFromBrowser = (id: string) => {
			const unsub = onSnapshot(doc(db, 'public', id), async (docSnapshot) => {
				if (docSnapshot.data()?.text !== '' && auth0IdToken) {
					const text = docSnapshot.data()?.text;
					setTextBefore(text);
					setInitialTextToTranslate(text);
					clearTextInFirestore(id);

					// the problem here is that autoDetectLang runs before auth0Token is received
					// when moving removing firebase dependency and refactoring autoTranslate logic, we should make sure this is not an issue
					await autoDetectLang(text, true);
				}
			});
			return () => unsub();
		};

		// Get the 'sid' query parameter from the URL
		let iFrameIdQueryParamFromUrl = new URL(window.location.href).searchParams.get('sid');

		// Set up the Firestore listener based on available ID:
		if (iFrameIdQueryParamFromUrl !== null && iFrameIdQueryParamFromUrl.length !== 0) {
			displayHighlightedTextFromBrowser(iFrameIdQueryParamFromUrl);
			setIFrameQueryParam(iFrameIdQueryParamFromUrl);
		} else if (iFrameQueryParam) {
			displayHighlightedTextFromBrowser(iFrameQueryParam);
		} else {
			console.warn('No iFrameQueryParam found.');
		}
		// auth0IdToken dependency here, as a quickfix to the problem related to authDetectLang (line 46)
		// TODO: when removing firebase dependency, auth0 token dependency should be handled differently
	}, [auth0IdToken]);

};